/**
 * Export the chord sheet as a ChordPro (.cho) file.
 *
 * ChordPro format:
 *   {title: Song Name}
 *   {key: G}
 *   {capo: 2}
 *
 *   {start_of_verse}
 *   [G]Amazing [D]grace, how [Em]sweet the sound
 *   {end_of_verse}
 */

import { buildRenderableSongModel } from './alignmentLayout.js';

const SECTION_TAGS = {
  verse:    { start: 'start_of_verse',  end: 'end_of_verse'  },
  chorus:   { start: 'start_of_chorus', end: 'end_of_chorus' },
  bridge:   { start: 'start_of_bridge', end: 'end_of_bridge' },
  intro:    { start: 'start_of_verse',  end: 'end_of_verse'  },
  outro:    { start: 'start_of_verse',  end: 'end_of_verse'  },
  prechorus:{ start: 'start_of_prechorus', end: 'end_of_prechorus' },
};

function sectionTag(label) {
  const normalized = label.replace(/\[|\]|\s*\d+\s*/g, '').toLowerCase().trim();
  return SECTION_TAGS[normalized] || { start: `start_of_${normalized}`, end: `end_of_${normalized}` };
}

function standaloneSectionChords(alignment, lineIndex) {
  const entries = (alignment || [])
    .filter((e) => e && e.chord && Number(e.lineIndex) === Number(lineIndex))
    .sort((a, b) => {
      const ta = Number.isFinite(Number(a.time)) ? Number(a.time) : Number.POSITIVE_INFINITY;
      const tb = Number.isFinite(Number(b.time)) ? Number(b.time) : Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      return Number(a.wordIndex || 0) - Number(b.wordIndex || 0);
    });
  if (!entries.length) return [];
  const byWord = new Map();
  entries.forEach((e) => {
    const wi = Number(e.wordIndex || 0);
    if (!byWord.has(wi)) byWord.set(wi, String(e.chord));
  });
  return [...byWord.entries()].sort((a, b) => a[0] - b[0]).map(([, chord]) => chord);
}

export function buildCanonicalExportRows(lyrics = '', alignment = []) {
  const rows = [];
  const lyricLines = (lyrics || '').split('\n');
  const hasIntroSection = lyricLines.some((l) => /^\[intro\]/i.test((l || '').trim()));
  const hasOutroSection = lyricLines.some((l) => /^\[outro\]/i.test((l || '').trim()));

  const introChords = standaloneSectionChords(alignment, -1);
  if (!hasIntroSection && introChords.length) {
    rows.push({ type: 'section', text: '[Intro]' });
    rows.push({ type: 'chord', text: introChords.join('  ') });
    rows.push({ type: 'blank', text: '' });
  }

  const model = buildRenderableSongModel(lyrics, alignment);
  model.forEach((row) => {
    if (row.type === 'blank') {
      rows.push({ type: 'blank', text: '' });
      return;
    }
    if (row.type === 'section') {
      rows.push({ type: 'section', text: row.text || '' });
      if (row.headerChordRow) rows.push({ type: 'chord', text: row.headerChordRow });
      return;
    }
    if (row.type === 'lyric') {
      if (row.chordRow) rows.push({ type: 'chord', text: row.chordRow });
      rows.push({ type: 'lyric', text: row.lyricRow || row.text || '' });
    }
  });

  const outroChords = standaloneSectionChords(alignment, -2);
  if (!hasOutroSection && outroChords.length) {
    rows.push({ type: 'blank', text: '' });
    rows.push({ type: 'section', text: '[Outro]' });
    rows.push({ type: 'chord', text: outroChords.join('  ') });
  }

  return rows;
}

/**
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.key
 * @param {number} opts.capo
 * @param {string} opts.lyrics
 * @param {Array<{chord: string, lineIndex: number, wordIndex: number}>} opts.alignment
 */
export function exportChordPro({ title, key, capo, lyrics, alignment }) {
  const lines = [];

  // Header directives
  if (title) lines.push(`{title: ${title}}`);
  if (key)   lines.push(`{key: ${key}}`);
  if (capo && capo > 0) lines.push(`{capo: ${capo}}`);
  lines.push('');

  const lyricLines = (lyrics || '').split('\n');

  // Build chord lookup: chordsByLine[lineIndex] = [{chord, wordIndex, syllableOffset}] sorted
  const chordsByLine = {};
  (alignment || []).forEach(({ chord, lineIndex, wordIndex, isSpacer, syllableOffset = 0, time }) => {
    if (isSpacer) return; // spacers are visual-only
    if (!chordsByLine[lineIndex]) chordsByLine[lineIndex] = [];
    chordsByLine[lineIndex].push({ chord, wordIndex, syllableOffset, time });
  });
  Object.values(chordsByLine).forEach(arr => arr.sort((a, b) => {
    if (a.wordIndex !== b.wordIndex) return a.wordIndex - b.wordIndex;
    return (a.syllableOffset || 0) - (b.syllableOffset || 0);
  }));

  let currentSectionTag = null;

  lyricLines.forEach((line, li) => {
    const sectionMatch = line.trim().match(/^\[(.+)\]$/);

    if (sectionMatch) {
      // Close previous section
      if (currentSectionTag) {
        lines.push(`{${currentSectionTag.end}}`);
        lines.push('');
      }
      const tag = sectionTag(line.trim());
      currentSectionTag = tag;
      lines.push(`{${tag.start}}`);
      lines.push(`# ${line.trim()}`);
      // Emit header-level chords (intro→lineIndex=-1, outro→-2, others→their lyricLine index)
      const sectionLower = line.trim().toLowerCase();
      let headerChords;
      if (/^\[intro/.test(sectionLower)) {
        headerChords = chordsByLine[-1] || chordsByLine[li];
      } else if (/^\[outro/.test(sectionLower)) {
        headerChords = chordsByLine[-2] || chordsByLine[li];
      } else {
        headerChords = chordsByLine[li];
      }
      if (headerChords && headerChords.length > 0) {
        lines.push(headerChords.map(({ chord }) => `[${chord}]`).join(' '));
      }
      return;
    }

    if (line.trim() === '') {
      lines.push('');
      return;
    }

    const lineChords = chordsByLine[li];
    if (!lineChords || lineChords.length === 0) {
      lines.push(line);
      return;
    }

    // Insert [Chord] markers before each word
    const words = line.split(/(\s+)/);
    let result = '';
    let realWordCount = 0;

    words.forEach(segment => {
      if (/\S/.test(segment)) {
        const assigned = lineChords.find(c => c.wordIndex === realWordCount);
        if (assigned) {
          result += `[${assigned.chord}]`;
        }
        result += segment;
        realWordCount++;
      } else {
        result += segment;
      }
    });

    lines.push(result);
  });

  // Close last section
  if (currentSectionTag) {
    lines.push(`{${currentSectionTag.end}}`);
  }

  const content = lines.join('\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(title || 'chord-sheet').replace(/\s+/g, '-').toLowerCase()}.cho`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export the chord sheet as a plain text file — chords above lyrics, Nashville style.
 *
 * @param {Object} opts
 * @param {string} opts.title
 * @param {string} opts.key
 * @param {number} opts.capo
 * @param {string} opts.lyrics
 * @param {Array<{chord: string, lineIndex: number, wordIndex: number}>} opts.alignment
 */
export function exportText({ title, key, capo, bpm, lyrics, alignment }) {
  const lines = [];

  if (title) lines.push(title);
  const line1 = [
    key ? `Key: ${key}` : null,
    bpm && bpm > 0 ? `BPM: ${bpm}` : null,
  ].filter(Boolean).join('  ');
  if (line1) lines.push(line1);
  if (capo && capo > 0) lines.push(`Capo: ${capo}`);
  if (title || line1 || (capo && capo > 0)) lines.push('');

  const renderRows = buildCanonicalExportRows(lyrics, alignment);
  renderRows.forEach((r) => lines.push(r.text || ''));

  const content = lines.join('\n');
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(title || 'chord-sheet').replace(/\s+/g, '-').toLowerCase()}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}
